import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { 
  FileText, 
  Search, 
  Trash2,
  Calendar,
  User,
  Link as LinkIcon,
  ExternalLink
} from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const HistoryPage = () => {
  const { api } = useAuth();
  const [orders, setOrders] = useState([]);
  const [sheets, setSheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [ordersRes, sheetsRes] = await Promise.all([
        api.get('/order-confirmations'),
        api.get('/function-sheets')
      ]);
      setOrders(ordersRes.data);
      setSheets(sheetsRes.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Historie');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteOrder = async () => {
    if (!deleteId) return;
    
    try {
      await api.delete(`/order-confirmations/${deleteId}`);
      setOrders(orders.filter(o => o.id !== deleteId));
      toast.success('Auftragsbestätigung gelöscht');
    } catch (error) {
      toast.error('Löschen fehlgeschlagen');
    } finally {
      setDeleteId(null);
    }
  };

  const getLinkedSheet = (orderId) => {
    return sheets.find(s => s.order_confirmation_id === orderId);
  };

  const filteredOrders = orders.filter(order => 
    order.filename?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.uploaded_by_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-pulse text-burgundy">Wird geladen...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="history-page">
      {/* Header */}
      <div>
        <h1 className="font-serif text-3xl font-bold text-burgundy">Historie</h1>
        <p className="text-charcoal-500 mt-1">
          Alle hochgeladenen Auftragsbestätigungen und zugehörige Function Sheets
        </p>
      </div>

      {/* Search */}
      <Card className="shadow-card">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-charcoal-400" />
            <Input
              placeholder="Suche nach Dateiname oder Benutzer..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 border-stone-300"
              data-testid="history-search"
            />
          </div>
        </CardContent>
      </Card>

      {/* History Table */}
      {filteredOrders.length > 0 ? (
        <Card className="shadow-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-stone-50">
                <TableHead className="font-serif text-burgundy">Dateiname</TableHead>
                <TableHead className="font-serif text-burgundy">Hochgeladen von</TableHead>
                <TableHead className="font-serif text-burgundy">Datum</TableHead>
                <TableHead className="font-serif text-burgundy">Function Sheet</TableHead>
                <TableHead className="font-serif text-burgundy text-right">Aktionen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredOrders.map((order) => {
                const linkedSheet = getLinkedSheet(order.id);
                return (
                  <TableRow 
                    key={order.id} 
                    className="hover:bg-stone-50 transition-colors"
                    data-testid={`history-row-${order.id}`}
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <FileText className="h-5 w-5 text-burgundy flex-shrink-0" />
                        <span className="font-medium text-charcoal-800 truncate max-w-[200px]">
                          {order.filename}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 text-charcoal-600">
                        <User className="h-4 w-4" />
                        {order.uploaded_by_name}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 text-charcoal-600">
                        <Calendar className="h-4 w-4" />
                        {new Date(order.uploaded_at).toLocaleString('de-DE', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                    </TableCell>
                    <TableCell>
                      {linkedSheet ? (
                        <Link to={`/sheets/${linkedSheet.id}`}>
                          <Badge 
                            className={`cursor-pointer hover:opacity-80 ${
                              linkedSheet.status === 'published' 
                                ? 'bg-emerald-100 text-emerald-700' 
                                : 'bg-gold-100 text-gold-700'
                            }`}
                          >
                            <LinkIcon className="h-3 w-3 mr-1" />
                            {linkedSheet.va_facts.title || 'Verknüpft'}
                          </Badge>
                        </Link>
                      ) : (
                        <Badge variant="outline" className="text-charcoal-400">
                          Nicht verknüpft
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-2">
                        {linkedSheet && (
                          <Link to={`/sheets/${linkedSheet.id}`}>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              className="text-charcoal-600 hover:text-burgundy"
                            >
                              <ExternalLink className="h-4 w-4" />
                            </Button>
                          </Link>
                        )}
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => setDeleteId(order.id)}
                          className="text-charcoal-600 hover:text-red-600"
                          data-testid={`delete-order-${order.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      ) : (
        <Card className="shadow-card">
          <CardContent className="py-12 text-center">
            <FileText className="h-16 w-16 mx-auto text-stone-300 mb-4" />
            <h3 className="font-serif text-xl text-charcoal-700 mb-2">
              {searchTerm ? 'Keine Ergebnisse gefunden' : 'Noch keine Uploads'}
            </h3>
            <p className="text-charcoal-500">
              {searchTerm 
                ? 'Versuchen Sie eine andere Suche' 
                : 'Laden Sie eine Auftragsbestätigung hoch, um zu beginnen'
              }
            </p>
          </CardContent>
        </Card>
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-burgundy">
              Auftragsbestätigung löschen?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Die PDF-Datei wird gelöscht. Ein zugehöriges Function Sheet bleibt erhalten.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-stone-300">Abbrechen</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteOrder}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default HistoryPage;
